import type { OrbitalEntity } from "../../domain/mesh";
import { minutesAgo } from "./time";

export function createMockOrbitals(origin = Date.now()): OrbitalEntity[] {
  const ago = (minutes: number) => minutesAgo(minutes, origin);
  return [
    {
      id: "goals-do",
      parentId: "goals",
      copyKey: "goalsDo",
      kind: "runtime",
      status: "online",
      cluster: "projects",
      metrics: { version: "v1.7.9", updatedAt: ago(18), meta: "ORDERED EXECUTION" },
    },
    {
      id: "goals-d1",
      parentId: "goals",
      copyKey: "goalsD1",
      kind: "storage",
      status: "online",
      cluster: "projects",
      metrics: { version: "D1", updatedAt: ago(24), meta: "PERSISTENCE LAYER" },
    },
    {
      id: "goals-futures-ui",
      parentId: "goals",
      copyKey: "goalsFuturesUi",
      kind: "service",
      status: "active",
      cluster: "projects",
      metrics: { version: "WEBGPU / UI", updatedAt: ago(6), meta: "PUBLIC INTERFACE" },
    },
    {
      id: "goals-ledger",
      parentId: "goals",
      copyKey: "goalsLedger",
      kind: "artifact",
      status: "online",
      cluster: "projects",
      metrics: { count: 486, updatedAt: ago(12), meta: "APPEND-ONLY" },
    },
    {
      id: "kaim-worker",
      parentId: "kaim-dev",
      copyKey: "kaimWorker",
      kind: "runtime",
      status: "online",
      cluster: "core",
      metrics: { version: "917cc61", updatedAt: ago(128), meta: "PUBLIC EDGE" },
    },
    {
      id: "kaim-studio-api",
      parentId: "kaim-dev",
      copyKey: "kaimStudioApi",
      kind: "service",
      status: "online",
      cluster: "core",
      metrics: { version: "/api/studio/*", updatedAt: ago(56), meta: "CONTROL SURFACE" },
    },
    {
      id: "kaim-access",
      parentId: "kaim-dev",
      copyKey: "kaimAccess",
      kind: "integration",
      status: "active",
      cluster: "infrastructure",
      metrics: { version: "ZT", updatedAt: ago(82), meta: "GUARDED" },
    },
    {
      id: "kaim-design-system",
      parentId: "kaim-dev",
      copyKey: "kaimDesignSystem",
      kind: "library",
      status: "online",
      cluster: "core",
      metrics: { version: "KX.UI", updatedAt: ago(37), meta: "SHARED TOKENS" },
    },
    {
      id: "kaim-nexus-connector",
      parentId: "kaim-dev",
      copyKey: "kaimNexusConnector",
      kind: "integration",
      status: "active",
      cluster: "infrastructure",
      metrics: { version: "schema/1", updatedAt: ago(11), meta: "MACHINE GRAPH SURFACE" },
    },
    {
      id: "build-brief",
      parentId: "build",
      copyKey: "buildBrief",
      kind: "artifact",
      status: "active",
      cluster: "life",
      metrics: { version: "v3", updatedAt: ago(4), meta: "PROGRAM LOCKED" },
    },
    {
      id: "build-documents",
      parentId: "build",
      copyKey: "buildDocuments",
      kind: "service",
      status: "active",
      cluster: "life",
      metrics: { count: 42, updatedAt: ago(16), meta: "INDEXED" },
    },
    {
      id: "build-plot-intel",
      parentId: "build",
      copyKey: "buildPlotIntel",
      kind: "integration",
      status: "online",
      cluster: "projects",
      metrics: { version: "2.0", updatedAt: ago(44), meta: "PARCEL-LAYER" },
    },
    {
      id: "build-timeline",
      parentId: "build",
      copyKey: "buildTimeline",
      kind: "service",
      status: "building",
      cluster: "life",
      metrics: { progress: 8, updatedAt: ago(10), meta: "PRE-CONSTRUCTION" },
    },
    {
      id: "photos-archive-index",
      parentId: "photos",
      copyKey: "photosArchiveIndex",
      kind: "service",
      status: "online",
      cluster: "memory",
      metrics: { count: 20769, updatedAt: ago(410), meta: "MATERIALIZED INDEX" },
    },
    {
      id: "photos-object-store",
      parentId: "photos",
      copyKey: "photosObjectStore",
      kind: "storage",
      status: "online",
      cluster: "memory",
      metrics: { updatedAt: ago(410), meta: "OBJECT STORAGE" },
    },
    {
      id: "photos-roza-instance",
      parentId: "photos",
      copyKey: "photosRozaInstance",
      kind: "service",
      status: "online",
      cluster: "memory",
      metrics: { version: "roza.photos.kaim.dev", updatedAt: ago(410), meta: "PRIVATE INSTANCE" },
    },
    {
      id: "home-assistant",
      parentId: "home",
      copyKey: "homeAssistant",
      kind: "service",
      status: "draft",
      cluster: "infrastructure",
      metrics: { version: "HA", updatedAt: ago(90), meta: "CONTROL PLANE" },
    },
    {
      id: "home-physical-host",
      parentId: "home",
      copyKey: "homePhysicalHost",
      kind: "runtime",
      status: "draft",
      cluster: "infrastructure",
      metrics: { updatedAt: ago(90), meta: "NETTOP / LOCAL" },
    },
    {
      id: "home-remote-access",
      parentId: "home",
      copyKey: "homeRemoteAccess",
      kind: "integration",
      status: "draft",
      cluster: "infrastructure",
      metrics: { version: "smarthome.kaim.dev", updatedAt: ago(90), meta: "REMOTE ACCESS" },
    },
    {
      id: "okolica-cadastre-planning",
      parentId: "okolica",
      copyKey: "okolicaCadastrePlanning",
      kind: "integration",
      status: "active",
      cluster: "projects",
      metrics: { version: "ULDK / MPZP", updatedAt: ago(2380), meta: "CADASTRE + PLANNING" },
    },
    {
      id: "okolica-terrain-environment",
      parentId: "okolica",
      copyKey: "okolicaTerrainEnvironment",
      kind: "integration",
      status: "active",
      cluster: "projects",
      metrics: { updatedAt: ago(2380), meta: "TERRAIN + ENVIRONMENT" },
    },
    {
      id: "okolica-mobility",
      parentId: "okolica",
      copyKey: "okolicaMobility",
      kind: "integration",
      status: "active",
      cluster: "projects",
      metrics: { updatedAt: ago(2380), meta: "ROADS + ACCESS" },
    },
    {
      id: "okolica-evidence-export",
      parentId: "okolica",
      copyKey: "okolicaEvidenceExport",
      kind: "artifact",
      status: "active",
      cluster: "projects",
      metrics: { version: "GeoJSON", updatedAt: ago(2380), meta: "EVIDENCE EXPORT" },
    },
    {
      id: "breakglass-edge-runtime",
      parentId: "breakglass",
      copyKey: "breakglassEdgeRuntime",
      kind: "runtime",
      status: "online",
      cluster: "projects",
      metrics: { version: "CF EDGE", updatedAt: ago(840), meta: "PUBLIC RUNTIME" },
    },
    {
      id: "breakglass-scenario-engine",
      parentId: "breakglass",
      copyKey: "breakglassScenarioEngine",
      kind: "service",
      status: "online",
      cluster: "projects",
      metrics: { updatedAt: ago(840), meta: "SCENARIO REGISTRY" },
    },
  ];
}
